import './button-group-item.scss';
import React, { PropsWithChildren, useRef } from 'react';
import { CheckboxGroupState, RadioGroupState } from 'react-stately';
import { cn } from '../../utils/helpers.ts';
export enum ButtonGroupInputType {
  Radio = 'radio',
  Checkbox = 'checkbox',
}
export type ButtonGroupItemBaseProps = PropsWithChildren<{
  ariaLabel?: string;
  className?: string;
  contentClassName?: string;
  id: string;
  indicatorClassName?: string;
  inputVariant: ButtonGroupInputType;
  value: string;
}>;
export type ButtonGroupItemHookResult = {
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  isSelected: boolean;
  isPressed: boolean;
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
};
export type ButtonGroupItemProps<TState> = ButtonGroupItemBaseProps & {
  useItemHook: (args: {
    value: string;
    ariaLabel?: string;
    id?: string;
  }, state: TState, ref: React.RefObject<HTMLInputElement | null>) => ButtonGroupItemHookResult;
  stateContext: React.Context<TState | null>;
};
export const ButtonGroupItem = <TState extends CheckboxGroupState | RadioGroupState,>({
  id,
  value,
  ariaLabel,
  className,
  contentClassName,
  indicatorClassName,
  children,
  useItemHook,
  stateContext,
  inputVariant
}: ButtonGroupItemProps<TState>) => {
  const state = React.useContext(stateContext) as TState;
  if (!state) {
    throw new Error('ButtonGroupItem must be used within a group provider');
  }
  const ref = useRef<HTMLInputElement | null>(null);
  const {
    inputProps,
    isSelected,
    labelProps
  } = useItemHook({
    id,
    value,
    ariaLabel
  }, state, ref);
  return <div className={cn([`ButtonGroupItem ButtonGroupItem--${inputVariant} relative flex justify-between`, className, {
    [`ButtonGroupItem--checked`]: isSelected
  }])}>
      <input {...inputProps} ref={ref} id={id} className="ButtonGroupItem__input sr-only" />

      <label {...labelProps} htmlFor={id} className="ButtonGroupItem__label absolute top-0 left-0 w-full h-full">
        <span className="sr-only">{value}</span>
      </label>

      <div className={cn('ButtonGroupItem__content', contentClassName)}>
        {children}
      </div>

      <div className={cn(`ButtonGroupItem__indicator absolute top-comfy-xs right-comfy-xs`, indicatorClassName)} aria-hidden="true" />
    </div>;
};